import { useCallback, useEffect, useMemo, useState } from 'react';
import { analyzeAlternativeLinks, commitAlternativeLinks, previewAlternativeLinks } from '../../../../../services/costPrice/alternativeLinkService';
import { AlternativeCategoryFilter, AlternativeHistoryWindow, AlternativeLinkPreview } from '../../../../../services/costPrice/alternativeLinkTypes';
import { useCostPriceStore } from '../../../../../stores/appCostPriceStore';
import { useSubrecipesStore } from '../../../../../stores/appSubrecipesStore';
import { useRestaurantScopeCurrent } from '../../../../../hooks/restaurantViewScope';
import { useAlternativeLinkReviewStore, AlternativeLinkSession } from '../../../../../stores/appAlternativeLinkReviewStore';
import { useWasteNeedsStore } from '../../../../../stores/appWasteNeedsStore';
import { AlternativeReviewState, buildAlternativePreviewRequest, createAlternativeReview, mergeAlternativeScan, parentKeyFor, refreshAlternativeParents, splitAlternativeCommit } from './alternativeLinkReview';

export function useAlternativeLinkReview(restaurantId: string, isOpen: boolean, selectedProductKeys?: string[]) {
  const restaurantScopeCurrent = useRestaurantScopeCurrent();
  const selectionSignature = selectedProductKeys ? JSON.stringify(Array.from(new Set(selectedProductKeys)).sort()) : '';
  const selection = useMemo<string[] | undefined>(() => selectionSignature ? JSON.parse(selectionSignature) : undefined, [selectionSignature]);
  // The two entry points never reuse each other's drafts or overwrite a different selection.
  const sessionKey = selection ? JSON.stringify([restaurantId, 'unlinked', selection]) : restaurantId;
  const session = useAlternativeLinkReviewStore(store => store.sessions[sessionKey]);
  const sessionId = session?.id;
  const state = session?.state ?? null;
  const window = selection ? 'all' : session?.window ?? 'two_months';
  const categories = session?.categories ?? [];
  const categoryFilter = session?.categoryFilter ?? 'all';
  const busy = session?.busy ?? null;
  const error = session?.error ?? null;
  const reloadFailed = session?.reloadFailed ?? false;
  const results = session?.results ?? [];
  const [preview, setPreview] = useState<{ sessionId: number; signature: string; value: AlternativeLinkPreview } | null>(null);
  const [previewing, setPreviewing] = useState(false);
  useEffect(() => { if (isOpen) useAlternativeLinkReviewStore.getState().ensure(sessionKey); }, [sessionKey, isOpen, sessionId]);

  const currentSession = useCallback(() => {
    const current = useAlternativeLinkReviewStore.getState().sessions[sessionKey];
    return current?.id === sessionId ? current : undefined;
  }, [sessionKey, sessionId]);
  const patch = useCallback((fn: (previous: AlternativeLinkSession) => AlternativeLinkSession) => {
    if (sessionId !== undefined) useAlternativeLinkReviewStore.getState().update(sessionKey, sessionId, fn);
  }, [sessionKey, sessionId]);
  const update = useCallback((fn: (previous: AlternativeReviewState) => AlternativeReviewState) => {
    patch(previous => previous.state ? { ...previous, state: fn(previous.state) } : previous);
  }, [patch]);
  const setWindow = (value: AlternativeHistoryWindow) => patch(previous => previous.state || previous.busy ? previous : { ...previous, window: value });
  const setCategoryFilter = (value: AlternativeCategoryFilter) => patch(previous => previous.state || previous.busy ? previous : { ...previous, categoryFilter: value });
  const setCategories = (value: string[]) => patch(previous => previous.state || previous.busy ? previous : { ...previous, categories: Array.from(new Set(value)).sort() });
  const setBusy = (value: AlternativeLinkSession['busy']) => patch(previous => ({ ...previous, busy: value }));
  const setError = useCallback((value: string | null) => patch(previous => ({ ...previous, error: value })), [patch]);
  const setReloadFailed = (value: boolean) => patch(previous => ({ ...previous, reloadFailed: value }));
  const reset = () => useAlternativeLinkReviewStore.getState().reset(sessionKey);
  const scopeCurrent = useCallback(() => !!currentSession() && restaurantScopeCurrent(restaurantId), [restaurantId, currentSession, restaurantScopeCurrent]);
  const live = useCallback(() => !!currentSession(), [currentSession]);
  const requestData = useMemo(() => state ? buildAlternativePreviewRequest(state) : null, [state]);
  const signature = requestData ? JSON.stringify(requestData.request) : '';
  const hasSelection = !!requestData && Object.values(requestData.request.selectedOccurrences).some(ids => ids.length > 0);

  useEffect(() => {
    setPreview(null);
    if (!state || !isOpen || !hasSelection || requestData?.invalidInput || busy) { setPreviewing(false); return; }
    const abort = new AbortController();
    setPreviewing(true);
    const timer = setTimeout(async () => {
      try {
        const value = await previewAlternativeLinks(JSON.parse(signature), abort.signal);
        if (!abort.signal.aborted && live()) { setPreview({ sessionId: sessionId!, signature, value }); setError(null); }
      } catch (cause) {
        if (!abort.signal.aborted && live()) { console.error('Alternative preview failed', cause); setError('previewFailed'); }
      } finally { if (!abort.signal.aborted && live()) setPreviewing(false); }
    }, 350);
    return () => { clearTimeout(timer); abort.abort(); };
  }, [state, signature, isOpen, hasSelection, requestData?.invalidInput, busy, sessionId, live, setError]);

  const analyze = async () => {
    const session = currentSession();
    if (!session || session.busy || !scopeCurrent()) return;
    if (session.state && !session.state.plan.remainingSourceKeys.length) return;
    session.controller?.abort();
    const abort = new AbortController(); patch(previous => ({ ...previous, controller: abort }));
    setBusy('scan'); setError(null); setPreview(null);
    try {
      const plan = await analyzeAlternativeLinks({ restaurantId, historyWindow: selection ? 'all' : session.window, categoryFilter: session.categoryFilter, ...(session.categories.length ? { categories: session.categories } : {}), ...(selection ? { selectedProductKeys: selection } : {}), ...(session.state ? { sourceKeys: session.state.plan.remainingSourceKeys } : {}) }, abort.signal);
      if (!live() || abort.signal.aborted || plan.restaurantId !== restaurantId || (plan.categoryFilter ?? 'all') !== session.categoryFilter) return;
      patch(previous => ({ ...previous, state: previous.state ? mergeAlternativeScan(previous.state, plan) : createAlternativeReview(plan) }));
    } catch (cause) {
      if (live() && !abort.signal.aborted) { console.error('Alternative analysis failed', cause); setError('scanFailed'); }
    } finally { if (live()) setBusy(null); }
  };

  const staleIssueKeys = (preview?.sessionId === sessionId && preview?.signature === signature ? preview.value.issues : []).filter(issue => ['stale_parent', 'deleted_parent', 'invalid_parent', 'invalid_occurrence', 'stale_candidate', 'unapproved_candidate', 'invalid_candidate'].includes(issue.code));
  const affectedKeys = Array.from(new Set([
    ...(state?.staleParents ?? []),
    ...(staleIssueKeys ?? []).flatMap(issue => issue.parentKey ? [issue.parentKey] : state?.plan.parents.map(p => p.key) ?? []),
    ...(state?.staleCandidates.length ? state.plan.parents.map(p => p.key) : []),
  ])).filter(key => !state?.appliedParents.includes(key));

  const refresh = async () => {
    const session = currentSession();
    if (!session?.state || session.busy || !scopeCurrent()) return;
    const current = session.state;
    const keys = affectedKeys.length ? affectedKeys : current.plan.parents.filter(p => !current.appliedParents.includes(p.key)).map(p => p.key);
    const request = buildAlternativePreviewRequest(current).request;
    request.selectedOccurrences = {};
    request.equivalences = {}; request.quantityOverrides = {}; request.wasteOverrides = {};
    const abort = new AbortController(); patch(previous => ({ ...previous, controller: abort }));
    setBusy('refresh'); setError(null); setPreview(null);
    try {
      for (let offset = 0; offset < keys.length; offset += 100) {
        const batchKeys = keys.slice(offset, offset + 100);
        request.refreshParents = current.plan.parents.filter(p => batchKeys.includes(p.key)).map(p => ({ kind: p.kind, id: p.id }));
        const value = await previewAlternativeLinks(request, abort.signal);
        if (!live() || abort.signal.aborted) break;
        update(previous => refreshAlternativeParents(previous, value.plan, batchKeys));
      }
    } catch (cause) {
      if (live() && !abort.signal.aborted) { console.error('Alternative refresh failed', cause); setError('refreshFailed'); }
    } finally { if (live()) setBusy(null); }
  };

  const matchingPreview = isOpen && preview?.sessionId === sessionId && preview?.signature === signature ? preview.value : null;
  const commitPlan = splitAlternativeCommit(matchingPreview?.parents ?? []);
  const canApply = !!matchingPreview && matchingPreview.newLinks > 0 && matchingPreview.issues.length === 0 && !affectedKeys.length && !busy && !previewing && !requestData?.invalidInput && commitPlan.oversized.length === 0;

  const apply = async (): Promise<boolean> => {
    const session = currentSession();
    if (!canApply || !session?.state || session.busy || !matchingPreview || !scopeCurrent()) return false;
    const current = session.state;
    // Recheck synchronously: an input edit must never submit the previous render's preview.
    if (JSON.stringify(buildAlternativePreviewRequest(current).request) !== signature) return false;
    setBusy('apply'); setError(null); setPreview(null);
    let allApplied = true;
    for (const parents of commitPlan.batches) {
      if (!live()) break;
      try {
        const response = await commitAlternativeLinks({ restaurantId, historyWindow: current.plan.historyWindow, categoryFilter: current.plan.categoryFilter ?? 'all', ...(current.plan.categories?.length ? { categories: current.plan.categories } : {}), ...(current.plan.selectedProductKeys ? { selectedProductKeys: current.plan.selectedProductKeys } : {}), parents });
        const successful = response.results.filter(r => r.status === 'added' || r.status === 'already_present');
        if (successful.length !== parents.length) allApplied = false;
        // Cache invalidation survives drawer closure. Reloads may publish only in the captured scope.
        if (successful.length) {
          useWasteNeedsStore.getState().invalidate(restaurantId);
          if (scopeCurrent()) {
            const loads = [];
            const reloadCosts = successful.some(r => r.kind === 'costPrice');
            const reloadPreps = successful.some(r => r.kind === 'subrecipe');
            if (reloadCosts) loads.push(useCostPriceStore.getState().load(restaurantId, { force: true, isCurrent: scopeCurrent }));
            if (reloadPreps) loads.push(useSubrecipesStore.getState().load(restaurantId, { force: true, isCurrent: scopeCurrent }));
            const loaded = await Promise.allSettled(loads);
            // Domain stores catch fetch errors and expose them in state instead of rejecting.
            const failed = loaded.some(r => r.status === 'rejected') || (reloadCosts && !!useCostPriceStore.getState().error) || (reloadPreps && !!useSubrecipesStore.getState().error);
            if (failed) { allApplied = false; if (live()) setReloadFailed(true); }
          }
        }
        if (!live()) break;
        patch(previous => ({ ...previous, results: [...previous.results, ...response.results] }));
        update(previous => ({ ...previous,
          staleParents: Array.from(new Set([...previous.staleParents, ...response.results.map(r => parentKeyFor(previous.plan.parents, r)).filter((key): key is string => !!key)])),
        }));
        // Refresh saved dishes without rerunning discovery, retaining their unreviewed alternatives.
        if (successful.length) {
          const keys = successful.map(r => parentKeyFor(current.plan.parents, r)).filter((key): key is string => !!key);
          const abort = new AbortController(); patch(previous => ({ ...previous, controller: abort }));
          try {
            const request = buildAlternativePreviewRequest(currentSession()!.state!).request;
            const refreshed = await previewAlternativeLinks({ ...request, selectedOccurrences: {}, selectedAlternatives: {},
              equivalences: {}, quantityOverrides: {}, wasteOverrides: {},
              refreshParents: successful.map(({ kind, id }) => ({ kind, id })),
            }, abort.signal);
            if (live() && !abort.signal.aborted) update(previous => refreshAlternativeParents(previous, refreshed.plan, keys));
          } catch (cause) {
            allApplied = false;
            if (live() && !abort.signal.aborted) {
              console.error('Alternative post-commit refresh failed', cause);
              setError('refreshFailed');
            }
          }
        }
      } catch (cause) {
        allApplied = false;
        console.error('Alternative commit interrupted', cause);
        // A lost response may have committed. Invalidate needs and require refreshed occurrences.
        useWasteNeedsStore.getState().invalidate(restaurantId);
        if (scopeCurrent()) {
          await Promise.allSettled([
            useCostPriceStore.getState().load(restaurantId, { force: true, isCurrent: scopeCurrent }),
            useSubrecipesStore.getState().load(restaurantId, { force: true, isCurrent: scopeCurrent }),
          ]);
        }
        if (live()) {
          setError('applyInterrupted');
          update(previous => ({ ...previous, staleParents: Array.from(new Set([...previous.staleParents, ...parents.map(p => parentKeyFor(previous.plan.parents, p)).filter((key): key is string => !!key)])) }));
        }
        break;
      }
    }
    if (live()) setBusy(null);
    return allApplied && scopeCurrent();
  };

  return { state, update, window, setWindow, categoryFilter, setCategoryFilter, categories, setCategories, busy, error: error ?? (reloadFailed ? 'reloadFailed' : null), preview: matchingPreview, previewing, invalidInput: requestData?.invalidInput, results, affectedKeys, canApply, oversized: commitPlan.oversized, analyze, refresh, apply, reset };
}
